import { ShieldIcon, UserIcon } from "lucide-react";
import { useState } from "react";
import { useAccessMatrix } from "@/admin/api";
import { startViewAs } from "@/auth/view-as";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLookup } from "@/entities/api";

const rowClass =
  "flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm outline-none hover:bg-muted focus-visible:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40";

/** Pick a user or a role to test Pulse as. Administrators only; the server enforces it. */
export function ViewAsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [search, setSearch] = useState("");
  const users = useLookup("app_user", search, null, { enabled: open });
  const matrix = useAccessMatrix();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>View Pulse as…</DialogTitle>
          <DialogDescription>
            Test access with another person’s roles, or one role on its own. Only this browser tab changes; use “Back
            to my access” in the banner to return.
          </DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="user">
          <TabsList>
            <TabsTrigger value="user">A user</TabsTrigger>
            <TabsTrigger value="role">A role</TabsTrigger>
          </TabsList>
          <TabsContent value="user" className="space-y-2 pt-2">
            <Input
              autoFocus
              placeholder="Search people…"
              aria-label="Search people"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="max-h-72 overflow-y-auto" role="list" aria-label="People">
              {users.data?.length === 0 && <p className="px-2.5 py-3 text-sm text-muted-foreground">No one found.</p>}
              {users.data?.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  role="listitem"
                  className={rowClass}
                  onClick={() => startViewAs({ kind: "user", id: u.id })}
                >
                  <UserIcon className="size-4 text-muted-foreground" aria-hidden="true" />
                  {u.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              A person’s roles come from their Entra groups as of their last sign-in.
            </p>
          </TabsContent>
          <TabsContent value="role" className="pt-2">
            <div className="max-h-80 overflow-y-auto" role="list" aria-label="Roles">
              {matrix.data?.roles.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  role="listitem"
                  className={rowClass}
                  onClick={() => startViewAs({ kind: "role", id: r.id })}
                >
                  <ShieldIcon className="size-4 text-muted-foreground" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{r.name}</span>
                    {r.description && (
                      <span className="block truncate text-xs text-muted-foreground">{r.description}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
            <p className="pt-2 text-xs text-muted-foreground">
              A role on its own: “Own” records are yours. Client roles see nothing without a client — view as a
              client user instead.
            </p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
